import axios from "axios";
import { createStore } from "devextreme-aspnet-data-nojquery";
import apiRoutes from "../../../assets/apiRoutes.json";
import { CONSOLIDATED_DISPATCHING_METHOD, KEY_FIELD } from "../constants";

const routes = apiRoutes.FM.DispatchManagement;

const apiUrl = (route) => `${window._env_.REACT_APP_APIS}/${route}`;

const authHeaders = (accessToken) => ({
    access_token: accessToken,
    Authorization: `Bearer ${accessToken}`,
});

/**
 * The board's stores.
 *
 * `createStore`, not `createConcurrencyAwareStore`: the concurrency-aware variant turns a 409 on a
 * save into a grid refresh, and nothing on this board saves. None of these declare an insertUrl,
 * updateUrl or deleteUrl at all, so there is nothing for it to do.
 *
 * `onBeforeSend` carries auth headers and nothing else - no body rewriting, no forced content type
 * (Architecture.md rule 15). Parameters go through `loadParams`, which leaves DevExtreme's own paging,
 * sort and filter parameters intact.
 */
const readOnlyStore = (route, accessToken, { key = KEY_FIELD.ROW_ID, loadParams } = {}) =>
    createStore({
        key,
        loadUrl: apiUrl(route),
        loadParams,
        onBeforeSend: (method, ajaxOptions) => {
            ajaxOptions.headers = authHeaders(accessToken);
        },
    });

// ---- top tabs -----------------------------------------------------------------------------------
// The profile code reaches the server through loadParams, read from a getter so changing profile does
// not have to rebuild the store.

const boardStore = (route, accessToken, getProfileCode) =>
    readOnlyStore(route, accessToken, {
        loadParams: { profileCode: () => getProfileCode() ?? "" },
    });

export const getPickDispatchObjectsStore = (accessToken, getProfileCode) =>
    boardStore(routes.GetPickDispatchObjects, accessToken, getProfileCode);

export const getDelDispatchObjectsStore = (accessToken, getProfileCode) =>
    boardStore(routes.GetDelDispatchObjects, accessToken, getProfileCode);

export const getCurrentDispatchObjectsStore = (accessToken, getProfileCode) =>
    boardStore(routes.GetCurrentDispatchObjects, accessToken, getProfileCode);

// ---- bottom tabs that follow the selected row ---------------------------------------------------
// Callers must not build these until the parent key is a real Guid - see the placeholder guard in
// BottomTabs (Architecture.md rule 5, DevExtreme E1042).

/**
 * Bottom Details store.
 *
 * Carries the consolidated flag and the order key as well as the dispatch-object key, because the
 * Delphi switches query entirely when the selected row's order is consolidated: it then lists the
 * whole order's stops rather than this object's (uFMD0000.pas:4787). The server decides which mode to
 * run; the client only reports what it has selected.
 */
export const getDispatchObjectDetailsStore = (accessToken, selectedRow) =>
    readOnlyStore(routes.GetDispatchObjectDetails, accessToken, {
        key: KEY_FIELD.LEGACY_ROW_ID,
        loadParams: {
            dispatchObjectRowId: () => selectedRow?.RowId,
            orderRowId: () => selectedRow?.OrderRowId ?? null,
            consolidated: () => selectedRow?.DispatchingMethod === CONSOLIDATED_DISPATCHING_METHOD,
        },
    });

export const getDispatchObjectContentsStore = (accessToken, dispatchObjectRowId) =>
    readOnlyStore(routes.GetDispatchObjectContents, accessToken, {
        loadParams: { dispatchObjectRowId: () => dispatchObjectRowId },
    });

/**
 * Bottom Messages store - row-scoped, unlike the reference lists below.
 *
 * uFMM0042 declares a `DispatchRowID` parameter and both halves of its UNION ALL end in
 * `WHERE FMDispatchObject.ROWID = :DispatchRowID`; uFMD0000.pas:4840 re-parameterises it on every
 * board-row scroll. Unscoped, this tab listed every message in the company.
 */
export const getDispatchMessagesStore = (accessToken, dispatchObjectRowId) =>
    readOnlyStore(routes.GetDispatchMessages, accessToken, {
        loadParams: { dispatchObjectRowId: () => dispatchObjectRowId },
    });

// ---- bottom reference lists ---------------------------------------------------------------------
// Not scoped to the selected row. In the Delphi these are the lists a dispatcher drags between panes,
// and pagBottomDispatchChange just reopens them (uFMD0000.pas:8612).

/** Bottom Pick and Del read the same unscoped live dispatch-object list (RECORDTYPE 'R'). */
export const getBottomDispatchObjectsStore = (accessToken) =>
    readOnlyStore(routes.GetBottomDispatchObjects, accessToken);

/**
 * Bottom Empty Container - a different record set, not a different view of the same rows.
 *
 * The Delphi gives this tab its own query whose whole purpose is RECORDTYPE = 'C'
 * (uFMD0000.pas doFMDOContainerBottomModuleCreate). Pointing it at the Pick/Del store listed every
 * live dispatch object instead of the containers, which is why the tab showed many times the legacy
 * row count.
 */
export const getEmptyContainerDispatchObjectsStore = (accessToken) =>
    readOnlyStore(routes.GetEmptyContainerDispatchObjects, accessToken);

export const getHistoricalDispatchObjectsStore = (accessToken) =>
    readOnlyStore(routes.GetHistoricalDispatchObjects, accessToken);

export const getBottomTripsStore = (accessToken) =>
    readOnlyStore(routes.GetBottomTrips, accessToken, { key: KEY_FIELD.LEGACY_ROW_ID });

export const getHistoricalTripsStore = (accessToken) =>
    readOnlyStore(routes.GetHistoricalTrips, accessToken, { key: KEY_FIELD.LEGACY_ROW_ID });

export const getTrucksStore = (accessToken) => readOnlyStore(routes.GetTrucks, accessToken);

export const getTrailersStore = (accessToken) => readOnlyStore(routes.GetTrailers, accessToken);

export const getDriversStore = (accessToken) => readOnlyStore(routes.GetDrivers, accessToken);

export const getShippersStore = (accessToken) => readOnlyStore(routes.GetShippers, accessToken);

// ---- profile ------------------------------------------------------------------------------------

/**
 * The Dispatching Data Profile the board runs under for the signed-in user.
 *
 * There is no picker: FMD0000 resolves the profile from the user's IDDISPATCHER record, so the board
 * does the same. A 204 means none resolved, which is a normal state - the grids then render uncoloured
 * and unnarrowed, matching the Delphi's `DispatchingProfile <> ''` guard. Resolving to null rather
 * than rejecting keeps that decision in one place instead of in every caller's catch.
 */
export const getEffectiveDispatchingProfile = (accessToken) =>
    axios
        .get(apiUrl(routes.GetEffectiveDispatchingProfile), { headers: authHeaders(accessToken) })
        .then((response) => (response.status === 204 ? null : response.data ?? null))
        .catch((error) => {
            if (error?.response?.status === 404 || error?.response?.status === 204) {
                return null;
            }
            throw error;
        });
